import React, { useState, useEffect, useMemo } from 'react';
import { 
  LuTrash2, LuMail, LuPhone, LuUser, LuMessageSquare, LuCalendar, 
  LuSearch, LuFilter, LuCircleCheck, LuDownload, LuX, LuHistory, LuClock
} from 'react-icons/lu';
import { CSVLink } from 'react-csv';
import axios from 'axios';
import moment from 'moment';
import SkeletonLoader from '../../components/ui/SkeletonLoader';
import StatCard, { StatGrid } from '../../components/ui/StatCard';
import FilterCard from '../../components/ui/FilterCard';
import DataTable, { TableEmpty } from '../../components/ui/DataTable';
import EmptyState from '../../components/ui/EmptyState';

const ContactManagement = ({ onCountChange }) => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedContact, setSelectedContact] = useState(null);
  
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);

  useEffect(() => {
    fetchContacts();
  }, []);

  const fetchContacts = async () => {
    const token = localStorage.getItem('adminToken');
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/contact/admin/all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setContacts(response.data.data);
        if (typeof onCountChange === 'function') {
          onCountChange(response.data.data.length);
        }
      }
    } catch (error) {
      setError('Failed to fetch contacts');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this contact?')) return;
    const token = localStorage.getItem('adminToken');
    try {
      await axios.delete(`${process.env.REACT_APP_API_BASE_URL}/api/contact/admin/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchContacts();
    } catch (error) {
      setError('Failed to delete contact');
    }
  };

  const filteredContacts = useMemo(() => {
    return contacts.filter(contact => {
      const query = searchQuery.toLowerCase();
      return !searchQuery ||
        contact.name?.toLowerCase().includes(query) ||
        contact.email?.toLowerCase().includes(query) ||
        contact.phone?.toLowerCase().includes(query) ||
        contact.comment?.toLowerCase().includes(query);
    });
  }, [contacts, searchQuery]);

  const toggleSelectAll = () => {
    if (selectedIds.length === filteredContacts.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(filteredContacts.map(r => r._id));
    }
  };

  const toggleSelectOne = (id) => {
    if (selectedIds.includes(id)) {
      setSelectedIds(selectedIds.filter(i => i !== id));
    } else {
      setSelectedIds([...selectedIds, id]);
    }
  };

  const csvData = useMemo(() => {
    const dataToExport = selectedIds.length > 0 
      ? filteredContacts.filter(r => selectedIds.includes(r._id))
      : filteredContacts;
    return dataToExport.map(r => ({
      Name: r.name,
      Email: r.email,
      Phone: r.phone,
      Message: r.comment,
      SubmittedAt: moment(r.createdAt).format('YYYY-MM-DD HH:mm:ss')
    }));
  }, [filteredContacts, selectedIds]);

  if (loading) {
    return <SkeletonLoader variant="table" rows={8} cols={5} />;
  }

  return (
    <div className="p-6 space-y-6">
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 px-4 py-3 rounded-xl text-sm">
          {error}
        </div>
      )}

      <StatGrid>
        <StatCard label="Total Contacts" value={contacts.length} icon={LuUser} />
        <StatCard label="Today" value={contacts.filter(r => moment(r.createdAt).isSame(moment(), 'day')).length} icon={LuClock} iconBg="bg-green-100 dark:bg-green-900/30" iconColor="text-green-600 dark:text-green-400" />
        <StatCard label="With Phone" value={contacts.filter(r => r.phone).length} icon={LuPhone} iconBg="bg-blue-100 dark:bg-blue-900/30" iconColor="text-blue-600 dark:text-blue-400" />
        <StatCard label="Filtered" value={filteredContacts.length} icon={LuHistory} iconBg="bg-purple-100 dark:bg-purple-900/30" iconColor="text-purple-600 dark:text-purple-400" />
      </StatGrid>

      <FilterCard>
        <div className="flex flex-col md:flex-row gap-4 items-center">
          <div className="relative flex-1 w-full">
            <LuSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search name, email, phone..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="input-modern pl-11 py-2.5 text-sm"
            />
          </div>
          <div className="flex items-center gap-3 w-full md:w-auto">
            <button
              onClick={() => setSelectionMode(!selectionMode)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all whitespace-nowrap ${selectionMode ? "btn-premium !py-2.5" : "bg-gray-100 dark:bg-dark-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-dark-600"}`}
            >
              <LuCircleCheck className="w-4 h-4" />
              {selectionMode ? "Selection Active" : "Selection Mode"}
            </button>
            <CSVLink
              data={csvData}
              filename={`Contacts_${moment().format('YYYY-MM-DD')}.csv`}
              className="flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-xl text-sm font-medium hover:shadow-lg transition-all whitespace-nowrap"
            >
              <LuDownload className="w-4 h-4" />
              Export CSV {selectedIds.length > 0 ? `(${selectedIds.length})` : ''}
            </CSVLink>
          </div>
        </div>
      </FilterCard>

      <DataTable>
          <table className="w-full">
            <thead>
              <tr className="bg-gradient-to-r from-primary-600 to-primary-700 text-white">
                {selectionMode && <th className="px-4 py-4 text-center w-12"><input type="checkbox" onChange={toggleSelectAll} checked={selectedIds.length === filteredContacts.length && filteredContacts.length > 0} className="rounded" /></th>}
                <th className="px-4 py-4 text-left text-sm font-semibold">Name</th>
                <th className="px-4 py-4 text-left text-sm font-semibold">Email</th>
                <th className="px-4 py-4 text-left text-sm font-semibold">Phone</th>
                <th className="px-4 py-4 text-left text-sm font-semibold">Message</th>
                <th className="px-4 py-4 text-left text-sm font-semibold">Submitted</th>
                <th className="px-4 py-4 text-right text-sm font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-dark-700">
              {filteredContacts.length === 0 ? (
                <TableEmpty colSpan={selectionMode ? 7 : 6} preset="search" title="No Contacts Found" description="Try adjusting your search criteria." />
              ) : filteredContacts.map((contact) => (
                <tr
                  key={contact._id}
                  className={`hover:bg-gray-50 dark:hover:bg-dark-700 transition-colors cursor-pointer ${selectedIds.includes(contact._id) ? 'bg-primary-50/50 dark:bg-primary-900/20' : ''}`}
                  onClick={() => selectionMode ? toggleSelectOne(contact._id) : setSelectedContact(contact)}
                >
                  {selectionMode && <td className="px-4 py-3 text-center"><input type="checkbox" checked={selectedIds.includes(contact._id)} onChange={() => toggleSelectOne(contact._id)} onClick={(e) => e.stopPropagation()} className="rounded" /></td>}
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-primary-500 to-primary-600 text-white flex items-center justify-center text-sm font-semibold">{contact.name?.[0]?.toUpperCase()}</div>
                      <span className="font-medium text-gray-900 dark:text-white text-sm">{contact.name}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-primary-600 dark:text-primary-400">{contact.email}</td>
                  <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">{contact.phone || 'N/A'}</td>
                  <td className="px-4 py-3 max-w-xs text-sm text-gray-500 line-clamp-1">{contact.comment}</td>
                  <td className="px-4 py-3 text-sm text-gray-500">{moment(contact.createdAt).format('MMM DD, YYYY')}</td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={(e) => { e.stopPropagation(); setSelectedContact(contact); }} className="px-3 py-1.5 text-sm font-medium text-primary-600 border border-primary-200 dark:border-primary-800 rounded-lg hover:bg-primary-50 dark:hover:bg-primary-900/20">View</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
      </DataTable>

      {selectedContact && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setSelectedContact(null)}>
          <div className="bg-white dark:bg-dark-800 rounded-3xl shadow-2xl max-w-2xl w-full overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="p-6 border-b border-gray-100 dark:border-dark-700 flex items-center justify-between">
              <h2 className="text-xl font-bold">Contact Details</h2>
              <button onClick={() => setSelectedContact(null)} className="p-2 hover:bg-gray-100 rounded-xl"><LuX /></button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-gray-50 rounded-2xl"><label className="text-[10px] font-black uppercase text-gray-400">Name</label><p className="font-bold">{selectedContact.name}</p></div>
                <div className="p-4 bg-gray-50 rounded-2xl"><label className="text-[10px] font-black uppercase text-gray-400">Email</label><p className="font-bold">{selectedContact.email}</p></div>
              </div>
              <div className="p-4 bg-gray-50 rounded-2xl"><label className="text-[10px] font-black uppercase text-gray-400">Message</label><p className="font-medium italic leading-relaxed">"{selectedContact.comment}"</p></div>
            </div>
            <div className="p-6 border-t border-gray-100 flex justify-end gap-3">
              <button onClick={() => setSelectedContact(null)} className="px-6 py-2 bg-gray-100 rounded-xl font-bold">Close</button>
              <button onClick={() => { handleDelete(selectedContact._id); setSelectedContact(null); }} className="px-6 py-2 bg-red-600 text-white rounded-xl font-bold">Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContactManagement;
